import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { Pencil } from 'react-bootstrap-icons';
import { makeApiRequests } from '../../helpers/api';
import { ENDPOINTS, ROLE_ADMIN, ROLE_SUPER_ADMIN, ROLE_SALES_MANAGER } from '../../helpers/constants';
import { userHasAnyRole } from '../../helpers/roleHelpers';

export default function ContractAssigneeEdit({ contract, onSaved }) {
  const [open, setOpen] = useState(false);
  const id = contract?._id;
  useEffect(() => setOpen(false), [id]);
  if (!id || !userHasAnyRole(ROLE_ADMIN, ROLE_SUPER_ADMIN, ROLE_SALES_MANAGER)) return null;
  return <span onClick={event => event.stopPropagation()}>
    <Button type="button" size="sm" variant="link" aria-label="Assign salesperson" title="Assign salesperson" onClick={() => setOpen(true)}><Pencil size={16}/></Button>
    {open && <AssignmentDialog id={id} onClose={() => setOpen(false)} onSaved={record => { setOpen(false); if (onSaved) onSaved(record); }}/>}
  </span>;
}
const label = member => member.name + (member.reportingOnly ? ' · Reporting only (inactive)' : member.inactive ? ' · Inactive' : '');
function AssignmentDialog({ id, onClose, onSaved }) {
  const [query, setQuery] = useState(''), [members, setMembers] = useState([]), [meta, setMeta] = useState(null);
  const [selected, setSelected] = useState(null), [loading, setLoading] = useState(true), [busy, setBusy] = useState(false), [error, setError] = useState('');
  const inFlight = useRef(false), active = useRef(true), initialized = useRef(false);
  const endpoint = ENDPOINTS.CONTRACTS_WITH_ID(id);
  useEffect(() => () => { active.current = false; }, []);
  useEffect(() => {
    const controller = new AbortController(); let cancelled = false;
    setLoading(true); setError('');
    const timer = setTimeout(async () => {
      const result = await makeApiRequests({ endpoint: endpoint + '/assignees?q=' + encodeURIComponent(query.trim()), method: 'GET', signal: controller.signal });
      if (cancelled) return;
      setLoading(false);
      if (result.error) { setError(result.error); setMembers([]); return; }
      setMembers(result.response?.members || []);
      if (!initialized.current) { initialized.current = true; setMeta(result.response); setSelected(result.response?.current || null); }
    }, query ? 250 : 0);
    return () => { cancelled = true; clearTimeout(timer); controller.abort(); };
  }, [endpoint, query]);
  async function perform(create) {
    if (inFlight.current || !meta || (!create && !selected)) return;
    inFlight.current = true; setBusy(true); setError('');
    const result = await makeApiRequests({ endpoint: endpoint + (create ? '/reporting-user' : '/assignment'), method: create ? 'POST' : 'PUT',
      requestBody: create ? { name: query.trim() } : { userId: selected._id, expectedOwnerId: meta.ownerId, expectedSalesLocationId: meta.salesLocationId } });
    if (!active.current) return;
    inFlight.current = false; setBusy(false);
    if (result.error) { setError(result.error); return; }
    if (create) {
      if (!result.response?.member?._id) { setError('Creation was not confirmed. Search the name before trying again.'); return; }
      setSelected(result.response.member);
      setMembers(rows => [result.response.member, ...rows.filter(member => member._id !== result.response.member._id)]);
    } else if (result.response?._id === id) onSaved(result.response);
    else setError('Save was not confirmed. Close and reopen the picker to check.');
  }
  return <Modal show centered backdrop="static" onHide={() => { if (!inFlight.current) onClose(); }} aria-labelledby="assignment-title">
    <Modal.Header closeButton={!busy}><Modal.Title id="assignment-title">Assign salesperson</Modal.Title></Modal.Header>
    <Modal.Body>
      {error && <Alert variant="danger" role="alert">{error}</Alert>}
      <Form.Group controlId="assignee-query"><Form.Label>Find a person at this store</Form.Label><Form.Control autoFocus value={query} maxLength={120} disabled={busy} placeholder="Search by name, including former staff" onChange={event => setQuery(event.target.value)}/></Form.Group>
      <p className="small text-muted">Active team members appear first. Search a name to include inactive people and reporting placeholders.</p>
      {selected && <p>Selected: <strong>{label(selected)}</strong></p>}
      <div role="group" aria-label="Matching people" style={{ maxHeight: '35vh', overflowY: 'auto' }}>
        {loading ? <p role="status">Searching…</p> : members.length ? members.map(member => <Button key={member._id} type="button" block variant={selected?._id === member._id ? 'primary' : 'outline-secondary'} disabled={busy} aria-pressed={selected?._id === member._id} className="text-left" onClick={() => setSelected(member)}>{label(member)}</Button>) : <p>No matching people.</p>}
      </div>
      {meta?.canCreatePlaceholder && <div className="border-top mt-3 pt-3">
        <Button type="button" variant="outline-primary" disabled={busy || loading || query.trim().length < 2} onClick={() => perform(true)}>Create inactive reporting placeholder</Button>
        <p className="small text-muted mt-2 mb-0">Uses the name above. No login, invitation, or email. An existing matching person is reused. Then select Save assignment.</p>
      </div>}
    </Modal.Body>
    <Modal.Footer><Button variant="outline-secondary" disabled={busy} onClick={onClose}>Cancel</Button><Button disabled={busy || !meta || !selected} onClick={() => perform(false)}>{busy ? 'Saving…' : 'Save assignment'}</Button></Modal.Footer>
  </Modal>;
}
